(() => {
  const toNumber = (value, fallback = 0) => {
    const number = Number.parseInt(value, 10);
    return Number.isFinite(number) ? number : fallback;
  };

  const clampToRules = (input, quantity) => {
    const min = toNumber(input.min, 1);
    const max = input.max ? toNumber(input.max, Number.MAX_SAFE_INTEGER) : Number.MAX_SAFE_INTEGER;
    const step = Math.max(toNumber(input.step, 1), 1);
    let next = Math.min(Math.max(quantity, min), max);
    const remainder = (next - min) % step;

    if (remainder !== 0) {
      next += step - remainder;
    }

    return Math.min(next, max);
  };

  const updateBulkSummary = (root) => {
    const select = root.querySelector('[data-bulk-quantity-select]');
    const hiddenQuantity = root.querySelector('[data-bulk-quantity-hidden]');
    const unitsEl = root.querySelector('[data-bulk-summary-units]');
    const totalEl = root.querySelector('[data-bulk-summary-total]');
    const savingsEl = root.querySelector('[data-bulk-summary-savings]');
    const option = select?.selectedOptions?.[0];

    if (!select || !option) return;

    if (hiddenQuantity) hiddenQuantity.value = select.value;

    const units = option.dataset.totalUnits || select.value;
    const price = option.dataset.totalPrice || '';
    const discount = toNumber(option.dataset.discountPercent, 0);
    const unitLabel = option.dataset.unitLabel || 'Unit';
    const unitText = `${units} ${unitLabel}${units === '1' ? '' : 's'}`;

    if (unitsEl) unitsEl.textContent = unitText;
    if (totalEl) totalEl.textContent = `${price} total`;
    if (savingsEl) {
      savingsEl.textContent = discount > 0 ? `${discount}% off` : '';
      savingsEl.hidden = discount <= 0;
    }

    updateBulkPrice(root, option);
  };

  const captureOriginalPrice = (priceBlock) => {
    if (priceBlock.dataset.bulkPriceCaptured === 'true') return;

    const regular = priceBlock.querySelector('[data-product-price-regular]');
    const secondary = priceBlock.querySelector('[data-product-price-secondary]');
    const compare = priceBlock.querySelector('[data-product-price-compare]');

    priceBlock.dataset.bulkPriceCaptured = 'true';
    priceBlock.dataset.bulkOriginalRegular = regular?.textContent?.trim() || '';
    priceBlock.dataset.bulkOriginalCompare = compare?.textContent?.trim() || '';
    priceBlock.dataset.bulkOriginalSecondaryHidden = secondary?.hidden ? 'true' : 'false';
  };

  const restoreOriginalPrice = (priceBlock) => {
    const regular = priceBlock.querySelector('[data-product-price-regular]');
    const secondary = priceBlock.querySelector('[data-product-price-secondary]');
    const compare = priceBlock.querySelector('[data-product-price-compare]');

    if (regular) regular.textContent = priceBlock.dataset.bulkOriginalRegular || '';
    if (compare) compare.textContent = priceBlock.dataset.bulkOriginalCompare || '';
    if (secondary) secondary.hidden = priceBlock.dataset.bulkOriginalSecondaryHidden === 'true';
  };

  const updateBulkPrice = (root, option) => {
    const form = root.closest('form');
    const priceBlocks = form?.querySelectorAll('.product-summary-price');

    if (!priceBlocks || priceBlocks.length === 0) return;

    const quantity = toNumber(option.value, 1);
    const discount = toNumber(option.dataset.discountPercent, 0);
    const totalPrice = option.dataset.totalPrice || '';
    const originalTotalPrice = option.dataset.originalTotalPrice || '';

    priceBlocks.forEach((priceBlock) => {
      captureOriginalPrice(priceBlock);

      if (quantity <= 1 || totalPrice === '') {
        restoreOriginalPrice(priceBlock);
        return;
      }

      const regular = priceBlock.querySelector('[data-product-price-regular]');
      const secondary = priceBlock.querySelector('[data-product-price-secondary]');
      const compare = priceBlock.querySelector('[data-product-price-compare]');

      if (regular) regular.textContent = totalPrice;
      if (secondary && compare) {
        compare.textContent = discount > 0 ? originalTotalPrice : '';
        secondary.hidden = discount <= 0 || originalTotalPrice === '';
      }
    });
  };

  const findSelectedVariant = (form, variants) => {
    const optionInputs = Array.from(form.querySelectorAll('[data-product-summary-option-select]')).sort(
      (left, right) =>
        toNumber(left.dataset.productSummaryOptionPosition, 0) -
        toNumber(right.dataset.productSummaryOptionPosition, 0)
    );

    if (optionInputs.length === 0) return variants[0] || null;

    const selectedOptions = optionInputs.map((input) => input.value);
    return (
      variants.find(
        (variant) =>
          Array.isArray(variant.options) &&
          variant.options.length === selectedOptions.length &&
          variant.options.every((value, index) => value === selectedOptions[index])
      ) || null
    );
  };

  const updateQuantityInputRules = (form, variant) => {
    const input = form.querySelector('[data-product-qty-input]');
    const rule = variant?.quantityRule;

    if (!input || !rule) return;

    const min = toNumber(rule.min, 1);
    const step = Math.max(toNumber(rule.increment, 1), 1);

    input.min = String(min);
    input.step = String(step);

    if (rule.max === null || rule.max === undefined || rule.max === '') {
      input.removeAttribute('max');
    } else {
      input.max = String(rule.max);
    }

    input.value = String(clampToRules(input, toNumber(input.value, min)));
  };

  const renderQuantityRules = (container, rule) => {
    const rules = container.querySelector('[data-product-quantity-rules]');
    if (!rules || !rule) return;

    const parts = [];
    const increment = toNumber(rule.increment, 1);
    const min = toNumber(rule.min, 1);

    if (increment > 1) parts.push(`Multiples of ${increment}`);
    if (min > 1) parts.push(`Minimum of ${min}`);
    if (rule.max !== null && rule.max !== undefined && rule.max !== '') parts.push(`Maximum of ${rule.max}`);

    rules.textContent = parts.join(' | ');
    rules.hidden = parts.length === 0;
  };

  const renderVolumeTable = (container, variant) => {
    const note = container.querySelector('[data-product-volume-note]');
    const table = container.querySelector('[data-product-volume-table]');
    const list = container.querySelector('[data-product-volume-list]');
    const breaks = Array.isArray(variant?.quantityPriceBreaks) ? variant.quantityPriceBreaks : [];

    if (note) note.hidden = breaks.length === 0;
    if (!table || !list) return;

    if (breaks.length === 0 || !variant?.quantityRule) {
      table.hidden = true;
      list.innerHTML = '';
      return;
    }

    const rows = [
      `<li class="flex justify-between gap-4"><span>${variant.quantityRule.min}+</span><span>${variant.priceText} each</span></li>`,
      ...breaks.map(
        (priceBreak) =>
          `<li class="flex justify-between gap-4"><span>${priceBreak.minimumQuantity}+</span><span>${priceBreak.priceText} each</span></li>`
      ),
    ];

    list.innerHTML = rows.join('');
    table.hidden = false;
  };

  const initVolumePricing = (container) => {
    const form = container.closest('form');
    const json = container.querySelector('[data-product-volume-json]');

    if (!form || !json) return;

    let data;
    try {
      data = JSON.parse(json.textContent);
    } catch (error) {
      console.error('[bulk-pricing] Failed to parse volume pricing JSON.', error);
      return;
    }

    const variants = Array.isArray(data.variants) ? data.variants : [];
    const update = () => {
      const variant = findSelectedVariant(form, variants);
      if (!variant) return;

      updateQuantityInputRules(form, variant);
      renderQuantityRules(container, variant.quantityRule);
      renderVolumeTable(container, variant);
    };

    form.querySelectorAll('[data-product-summary-option-select]').forEach((input) => {
      input.addEventListener('change', update);
    });

    update();
  };

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest('[data-product-qty], [data-cart-qty]');
      if (!button) return;

      const root = button.closest('[data-quantity-stepper], .relative');
      const input = root?.querySelector('[data-product-qty-input], [data-cart-qty-input]');
      if (!input) return;

      const min = toNumber(input.min, 1);
      const step = Math.max(toNumber(input.step, 1), 1);
      const max = input.max ? toNumber(input.max, Number.MAX_SAFE_INTEGER) : Number.MAX_SAFE_INTEGER;
      if (min === 1 && step === 1 && max === Number.MAX_SAFE_INTEGER) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const current = clampToRules(input, toNumber(input.value, min));
      const next = button.matches('[data-product-qty="minus"], [data-cart-qty="minus"]')
        ? current - step
        : current + step;
      input.value = String(clampToRules(input, next));
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    },
    true
  );

  const init = () => {
    document.querySelectorAll('[data-bulk-quantity]').forEach((root) => {
      updateBulkSummary(root);
      root.querySelector('[data-bulk-quantity-select]')?.addEventListener('change', () => updateBulkSummary(root));
    });

    document.querySelectorAll('[data-product-volume-pricing]').forEach(initVolumePricing);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
